import { BarChartOutlined, LineChartOutlined, TableOutlined } from "@ant-design/icons";
import { css } from "@emotion/react";
import { LoadingComponent } from "@src/components/loading-custom";
import { AbLoadingTips, LoadingMessage } from "@src/constant/loading-message";
import { ChartDataTypeEnum, UseChartLayoutType } from "@src/hooks/ab-testing/use-chart-layout";
import { trackEventPayload } from "@src/util/track-event-payload";
import { Radio, Spin } from "antd";
import React from "react";
import { useTracking } from "react-tracking";
type ChartLayoutProps = {
    customTrackEvent?: (mode: string) => void;
    moreActions?: React.ReactNode | ((mode: ChartMode) => React.ReactNode);
    renderRaw?: React.ReactNode;
    renderChart?: React.ReactNode;
    renderOtherChart?: React.ReactNode;
    loading?: boolean;
    defaultMode?: "otherChart" | "chart" | "raw";
    hiddenMode?: boolean;
    isHasOtherChart?: boolean;
} & Partial<
    UseChartLayoutType & {
        chartKey: string;
    }
>;
type ChartMode = "otherChart" | "chart" | "raw";
enum ChartModeEnum {
    OTHER_CHART = "otherChart",
    CHART = "chart",
    RAW = "raw",
}

export const ChartLayoutWithUserPercent: React.FC<ChartLayoutProps> = ({
    customTrackEvent,
    moreActions,
    renderRaw,
    renderChart,
    renderOtherChart,
    loading = false,
    defaultMode,
    hiddenMode = false,
    isHasOtherChart = false,
    chartKey,
    ...useChartLayoutProps
}) => {
    const { trackEvent } = useTracking();

    const [mode, setMode] = React.useState<ChartMode>(defaultMode || "chart");
    const { chartDataType, setChartDataType } = useChartLayoutProps;

    if (loading) {
        return <LoadingComponent message={LoadingMessage} tips={AbLoadingTips} />;
    }
    return (
        <div
            css={css`
                .ant-tabs-nav-wrap {
                    justify-content: start;
                }
            `}
        >
            {typeof moreActions === "function" ? moreActions(mode) : moreActions}
            {mode === ChartModeEnum.OTHER_CHART && <div className="pt-6">{renderOtherChart}</div>}
            {mode === ChartModeEnum.RAW && <div className="pt-6">{renderRaw}</div>}
            {mode === ChartModeEnum.CHART && (
                <>
                    {chartDataType && setChartDataType && (
                        <Radio.Group
                            value={chartDataType}
                            onChange={(v) => {
                                setChartDataType?.(v.target.value);
                                if (customTrackEvent) {
                                    customTrackEvent(v.target.value);
                                } else {
                                    chartKey && trackEvent(trackEventPayload.chartTypeSelect(chartKey, v.target.value));
                                }
                            }}
                        >
                            <Radio.Button value={ChartDataTypeEnum.USER_PERCENT}>User Percent</Radio.Button>
                            <Radio.Button value={ChartDataTypeEnum.USER_COUNT}>User Count</Radio.Button>
                        </Radio.Group>
                    )}

                    <div className="pb-8" />
                    {renderChart}
                </>
            )}
            {!hiddenMode && (
                <div className="flex justify-center w-full mt-[40px]">
                    <Radio.Group
                        value={mode}
                        onChange={(v) => {
                            setMode?.(v.target.value);
                            if (customTrackEvent) {
                                customTrackEvent(v.target.value);
                            } else {
                                chartKey && trackEvent(trackEventPayload.chartModeSelect(chartKey, v.target.value));
                            }
                        }}
                    >
                        <Radio.Button value={ChartModeEnum.CHART}>
                            <BarChartOutlined />
                        </Radio.Button>
                        {isHasOtherChart && (
                            <Radio.Button value={ChartModeEnum.OTHER_CHART}>
                                <LineChartOutlined />
                            </Radio.Button>
                        )}
                        <Radio.Button value={ChartModeEnum.RAW}>
                            <TableOutlined />
                        </Radio.Button>
                    </Radio.Group>
                </div>
            )}
        </div>
    );
};
